import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import client from "@/api/client";

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export function PostCard({ post }) {
  const { t } = useTranslation();
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [liked, setLiked] = useState(false);

  const handleLike = async () => {
    if (liked) return;
    setLiked(true);
    setLikes((l) => l + 1);
    try {
      await client.post(`/community/posts/${post.post_id}/like`);
    } catch (e) {
      // Rollback optimistic update on failure and surface the error to logs.
      console.error("PostCard.handleLike failed:", e);
      setLiked(false);
      setLikes((l) => Math.max(0, l - 1));
    }
  };

  return (
    <Card className="rounded-2xl border border-border p-5" data-testid="community-post-card">
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={post.author?.picture} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm">{initials(post.author?.name)}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">{post.author?.name}</p>
            {post.author?.role && (
              <Badge variant="secondary" className="rounded-full text-[10px] font-normal capitalize">
                {t(`roles.${post.author.role}`)}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</p>
          <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
          <div className="mt-3">
            <Button variant="ghost" size="sm" className="gap-1.5 rounded-lg text-muted-foreground" onClick={handleLike} data-testid="post-like-button">
              <Heart className={`h-4 w-4 ${liked ? "fill-[color:var(--brand-primary)] text-[color:var(--brand-primary)]" : ""}`} /> {likes}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
